import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { checkContactDuplicates, } from '../infrastructure/adapters/contactService';
import { logger } from '../../../infrastructure/services/LoggerService';
import { EMAIL_REGEX } from '../../../shared/utils/validation';
export const DEDUP_PANEL_MAX = 6;
/** OP-5600: the deduplication engine triggers once the last name (Nom) reaches this length. */
export const DEDUP_MIN_LASTNAME_LENGTH = 3;
export function useContactDeduplication() {
    const { t } = useI18n();
    const dedupState = ref('initialHint');
    const dedupResults = ref([]);
    let debounceTimer = null;
    let abortController = null;
    /**
     * `true` whenever any candidate has an exact email match — shared OR not (OP-5601).
     * Drives the shared-email opt-in box + the green "associated address" indicator, together
     * with the workspace shared-email setting (CreateContactModal): the opt-in is only relevant
     * when the typed email already exists on another contact.
     */
    const hasExactEmailMatch = computed(() => dedupResults.value.some((r) => r.emailExactMatch));
    const dedupTitle = computed(() => {
        if (dedupState.value === 'noDuplicate')
            return t('contact.create.dedup.noDuplicate');
        if (dedupState.value === 'duplicateFound')
            return t('contact.create.dedup.duplicateFound', { count: dedupResults.value.length });
        if (dedupState.value === 'duplicateFoundShared')
            return t('contact.create.dedup.duplicateFoundShared', { count: dedupResults.value.length });
        if (dedupState.value === 'similarFound')
            return t('contact.create.dedup.similarFound', { count: dedupResults.value.length });
        return t('contact.create.dedup.title');
    });
    const dedupHint = computed(() => {
        switch (dedupState.value) {
            case 'initialHint': return t('contact.create.dedup.hint');
            case 'checking': return t('contact.create.dedup.checking');
            case 'noDuplicate': return t('contact.create.dedup.noDuplicateHint');
            case 'duplicateFound': return t('contact.create.dedup.duplicateHint');
            case 'duplicateFoundShared': return t('contact.create.dedup.sharedEmailHint');
            case 'similarFound': return t('contact.create.dedup.similarHint');
            default: return '';
        }
    });
    function resolveState(results) {
        if (results.length === 0)
            return 'noDuplicate';
        // An exact email match on a non-shared mailbox is a certain duplicate → blocks the CTA.
        if (results.some((r) => r.emailExactMatch && !r.emailIsShared))
            return 'duplicateFound';
        // An exact match on a shared mailbox is allowed → CTA stays active, informational message shown.
        if (results.some((r) => r.emailExactMatch && r.emailIsShared))
            return 'duplicateFoundShared';
        return 'similarFound';
    }
    async function runDeduplication(payload, triggers = {}) {
        // The check fires as soon as ANY of these is satisfied:
        //  - the last name (Nom) reaches 3 characters (OP-5600), OR
        //  - a valid email is entered/modified (OP-6023), OR
        //  - a valid phone number is entered/modified (OP-5869).
        // A valid email/phone alone must surface duplicate and shared-email matches even before
        // the name is filled in (or while it is still too short).
        const lastNameReady = (payload.lastName?.trim().length ?? 0) >= DEDUP_MIN_LASTNAME_LENGTH;
        // Shape only, deliberately NOT isValidEmail: this gates the dedup LOOKUP, not the save. A
        // blocked-TLD address (user@gmail.test) is still worth matching against existing channels —
        // the form's own isValidEmail check is what refuses it (OP-7542).
        const emailReady = !!payload.email && EMAIL_REGEX.test(payload.email.trim());
        const phoneReady = !!payload.phone?.trim() && (triggers.phoneValid ?? false);
        if (!lastNameReady && !emailReady && !phoneReady) {
            if (debounceTimer !== null)
                clearTimeout(debounceTimer);
            abortController?.abort();
            abortController = null;
            dedupState.value = 'initialHint';
            dedupResults.value = [];
            return;
        }
        if (debounceTimer !== null)
            clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            abortController?.abort();
            abortController = new AbortController();
            dedupState.value = 'checking';
            dedupResults.value = [];
            try {
                const results = await checkContactDuplicates(payload, abortController.signal);
                dedupResults.value = results;
                dedupState.value = resolveState(results);
            }
            catch (error) {
                if (axios.isCancel(error))
                    return;
                logger.error('Contact duplicate check failed', error);
                dedupState.value = 'initialHint';
                dedupResults.value = [];
            }
        }, 250);
    }
    function reset() {
        if (debounceTimer !== null) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        abortController?.abort();
        abortController = null;
        dedupState.value = 'initialHint';
        dedupResults.value = [];
    }
    return { dedupState, dedupResults, dedupTitle, dedupHint, hasExactEmailMatch, runDeduplication, reset };
}
